import React from "react";
import { css, useTheme } from "@emotion/react";
import { mediaQueries, pxToRem } from "@upsun/react-component-library/src/styles";
import Heading from "@upsun/react-component-library/src/components/Heading";
import colorsLight from "@upsun/react-component-library/src/styles/colors-light";
import colorsDark from "@upsun/react-component-library/src/styles/colors-dark";
import { UpsunTheme } from "@upsun/react-component-library/src/themes";
import Button from "@upsun/react-component-library/src/components/Button";

type Field = {
  name: string,
  type: string
  values: string[]
}

type Question = {
  label: string
  required: boolean
  fields: Field[]
}

type GreenhouseFormProps = {
  form: Question[]
  jobId: string
  setSubmit(status: number): void 
}

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const GreenhouseForm : React.FC<GreenhouseFormProps> = ({ form, jobId, setSubmit, ...props }) => {
  const fetch = require("node-fetch");
  const theme = useTheme() as UpsunTheme;

  const submitForm = async (formSubmit : any) => {
    if (typeof window !== `undefined`) {
      const grecaptcha = window.grecaptcha;
      grecaptcha.enterprise.ready(async () => {
        const token = await grecaptcha.enterprise.execute(
          "6LfDgmEpAAAAAL1uvkI2L8bHGOSBlGpyKkOlLzOZ",
          {
            action: "SUBMIT"
          }
        );

        try {
          const response = await fetch(`/apply/${jobId}?token=${token}`, {
            method: "POST",
            body: formSubmit
          });
          setSubmit(response.status);
        } catch (error) {
          setSubmit(401);
          console.error(error);
        }
      });
    }
  };

  let children;
  if (form) {
    children = form.map(question => {
      let element;
      switch (question.fields[0].type) {
        case "input_text":
          element = (
            <div key={question.fields[0].name}>
              <input
                type={question.fields[0].name === "email" ? "email" : "text"}
                name={question.fields[0].name}
                id={question.fields[0].name}
                required={question.required}
              />
              <label htmlFor={question.fields[0].name}>{question.label}</label>
            </div>
          );
          break;
        case "input_file":
          element = (
            <div key={question.fields[0].name}>
              <input
                type="file"
                id={question.fields[0].name}
                name={question.fields[0].name}
                accept=".pdf,.doc,.docx,.txt,.rtf,application/msword"
                required={question.required}
              />
              <label htmlFor={question.fields[0].name}>{question.label}</label>
            </div>
          );
          break;
        case "multi_value_single_select":
          element = (
            <div key={question.fields[0].name}>
              <select
                name={question.fields[0].name}
                id={question.fields[0].name}
                required={question.required}
              >
                <option value={""}>Please select</option>
                {question.fields[0].values.map((option : any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label htmlFor={question.fields[0].name}>{question.label}</label>
            </div>
          );
          break;
        default:
          element = <div key={question.fields[0].name} />;
      }

      return element;
    });
  }

  const formStyles = css`
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(16)};
    align-items: flex-end;
    background: ${theme.name === "light" ? colorsLight.NeutralBgDefault : colorsDark.NeutralBgDefault};
    padding: ${pxToRem(16)};
    border-radius: ${pxToRem(8)};

    h2 {
      align-self: flex-start;
    }

    label {
      display: block;
    }

    div {
      width: 100%;
      display: flex;
      flex-direction: column-reverse;

      input,
      select {
        width: 100%;
        margin-bottom: ${pxToRem(16)};
      }
    }

    input:required + label::after {
      content: " *";
      color: red;
    }

    select:required + label::after {
      content: " *";
      color: red;
    }

    input[type="submit"] {
      width: auto;
      margin: 0 auto;
    }
  `;

  return (
    <>
      <form
        css={formStyles}
        {...props}
        id={`greenhouse-form`}
        onSubmit={(e : any) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          submitForm(formData);
        }}
      >
        <Heading level={2} levelStyle={4} text={"Apply for this job"} />
        {children}
        <Button type="submit" text="Submit application" />
      </form>
    </>
  );
};
export default GreenhouseForm;
